import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useUpdateBundlePrices } from '~/hooks/apis/bundle/useBundles';

import { formatDateWeekDayShortMonth } from '~/services/TimeService';

import { Bundle, BundleComponent, BundlePrice } from '~/types/bundles/bundle';

import { checkInDate } from './hotelStay';
import { tourPrice } from './tourAvailability';
import useBundleDepartureChecks from './useBundleDepartureChecks';

interface Props {
  bundle: Bundle;
}

type Part = 'tour' | 'hotel';

const OCCUPANCY_LABELS: Record<number, string> = { 1: 'Single', 2: 'Double' };
const PART_LABELS: Record<Part, string> = { tour: 'Tour', hotel: 'Hotel' };
const ADJUSTMENT_PATTERN = /^-?\d+$/;

const aud = (amount: number | undefined | null) =>
  amount === undefined || amount === null ? '–' : `$${amount.toLocaleString('en-AU')}`;

const signedAud = (amount: number) =>
  amount === 0 ? '–' : `${amount < 0 ? '−' : '+'}$${Math.abs(amount).toLocaleString('en-AU')}`;

/** The component's price row for one occupancy and departure date. */
function priceRow(component: BundleComponent | undefined, occupancy: number, date: string): BundlePrice | undefined {
  return component?.rows.find((row) => row.occupancy === occupancy)?.prices.find((price) => price.date === date);
}

function AdjustmentField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <TextField
      size="small"
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      error={value !== '' && !ADJUSTMENT_PATTERN.test(value)}
      helperText="Whole dollars; negative is a discount"
      inputProps={{ inputMode: 'numeric' }}
      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
      fullWidth
    />
  );
}

/** Each departure's tour and hotel price, each component's adjustment, and what the bundle sells for. */
export default function BundlePricesStep({ bundle }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [occupancy, setOccupancy] = useState(2);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<{ anchor: HTMLElement; date: string } | null>(null);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [bulk, setBulk] = useState<Record<Part, string>>({ tour: '', hotel: '' });

  const { tour, hotels, departures, tourDepartureOn, hotelResults, blocked } = useBundleDepartureChecks(bundle);
  const hotel = hotels[0];
  const hotelRates = hotelResults[0]?.data?.[occupancy];
  const updatePrices = useUpdateBundlePrices(bundle.id);

  const valueOf = (price: BundlePrice | undefined) =>
    price ? (drafts[price.id] ?? String(price.price_adjustment)) : '';
  const amountOf = (price: BundlePrice | undefined) => Number(valueOf(price)) || 0;

  const rows = departures.map((date) => {
    const checkIn = hotel ? checkInDate(date, hotel.day_offset ?? 0) : undefined;
    const parts = { tour: priceRow(tour, occupancy, date), hotel: priceRow(hotel, occupancy, date) };
    const tourAmount = tourPrice(tourDepartureOn(date), occupancy);
    const hotelAmount = checkIn ? hotelRates?.[checkIn]?.price : undefined;
    const adjustment = amountOf(parts.tour) + amountOf(parts.hotel);
    const isPriced = tourAmount !== undefined && hotelAmount !== undefined && hotelAmount !== null;
    return {
      date,
      parts,
      tourAmount,
      hotelAmount,
      adjustment,
      total: isPriced ? tourAmount + hotelAmount + adjustment : undefined,
    };
  });

  const changed = Object.entries(drafts).filter(([id, value]) => {
    const saved = bundle.components
      .flatMap((component) => component.rows.flatMap((row) => row.prices))
      .find((price) => price.id === id);
    return ADJUSTMENT_PATTERN.test(value) && Number(value) !== saved?.price_adjustment;
  });
  const hasInvalid = Object.values(drafts).some((value) => value !== '' && !ADJUSTMENT_PATTERN.test(value));

  const setDraft = (price: BundlePrice | undefined, value: string) => {
    if (price) setDrafts((current) => ({ ...current, [price.id]: value }));
  };

  const applyToAll = () => {
    setDrafts((current) => {
      const next = { ...current };
      (Object.keys(bulk) as Array<Part>).forEach((part) => {
        if (!ADJUSTMENT_PATTERN.test(bulk[part])) return;
        rows.forEach((row) => {
          const price = row.parts[part];
          if (price) next[price.id] = bulk[part];
        });
      });
      return next;
    });
    setIsBulkOpen(false);
  };

  const handleSave = async () => {
    try {
      await updatePrices.mutateAsync({
        prices: changed.map(([id, value]) => ({ id, price_adjustment: Number(value) })),
      });
      // The saved bundle now carries these values, so the drafts are done with.
      setDrafts({});
      enqueueSnackbar('Price adjustments saved', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };

  if (!tour || !hotel || departures.length === 0) {
    return (
      <Box>
        <PageSubheader title="Prices" />
        <Alert severity="info">Add the tour, the hotel and the departures to sell before setting prices.</Alert>
      </Box>
    );
  }

  const editingRow = rows.find((row) => row.date === editing?.date);

  return (
    <Box>
      <PageSubheader title="Prices" />

      <Typography color="text.secondary" mb={2}>
        Live AUD prices. Each departure sells for the tour and hotel prices plus any adjustment.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={occupancy}
          onChange={(_event, next: number | null) => next && setOccupancy(next)}
        >
          {[1, 2].map((value) => (
            <ToggleButton key={value} value={value} sx={{ px: 2 }}>
              {OCCUPANCY_LABELS[value]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box flex={1} />
        <Button startIcon={<TuneIcon />} onClick={() => setIsBulkOpen(true)}>
          Adjust all departures
        </Button>
      </Stack>

      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Departs</TableCell>
              <TableCell align="right">Tour</TableCell>
              <TableCell align="right">Hotel</TableCell>
              <TableCell align="right">Adjustment</TableCell>
              <TableCell align="right">Bundle price</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const isBlocked = blocked.includes(row.date);
              return (
                <TableRow key={row.date} hover sx={{ opacity: isBlocked ? 0.55 : 1 }}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{formatDateWeekDayShortMonth(row.date)}</span>
                      {isBlocked && (
                        <Tooltip title="Can't be sold: the tour or the hotel can't take a single and a double">
                          <WarningAmberIcon fontSize="small" color="warning" />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{aud(row.tourAmount)}</TableCell>
                  <TableCell align="right">{aud(row.hotelAmount)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      <Typography
                        variant="body2"
                        color={
                          row.adjustment < 0 ? 'success.main' : row.adjustment > 0 ? 'text.primary' : 'text.secondary'
                        }
                      >
                        {signedAud(row.adjustment)}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label={`Adjust ${row.date}`}
                        onClick={(event) => setEditing({ anchor: event.currentTarget, date: row.date })}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">{aud(row.total)}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Popover
        open={!!editing}
        anchorEl={editing?.anchor}
        onClose={() => setEditing(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {!!editingRow && (
          <Stack spacing={2} p={2} width={280}>
            <Typography variant="subtitle2">
              {formatDateWeekDayShortMonth(editingRow.date)} · {OCCUPANCY_LABELS[occupancy]}
            </Typography>
            {(['tour', 'hotel'] as Array<Part>).map((part) => (
              <AdjustmentField
                key={part}
                label={`${PART_LABELS[part]} adjustment`}
                value={valueOf(editingRow.parts[part])}
                onChange={(value) => setDraft(editingRow.parts[part], value)}
              />
            ))}
            <Box textAlign="right">
              <Button onClick={() => setEditing(null)}>Done</Button>
            </Box>
          </Stack>
        )}
      </Popover>

      <Dialog open={isBulkOpen} onClose={() => setIsBulkOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Adjust all departures · {OCCUPANCY_LABELS[occupancy]}</DialogTitle>
        <DialogContent>
          <DialogContentText mb={2}>
            Sets the adjustment on every departure. Leave a field empty to keep each departure's own.
          </DialogContentText>
          <Stack spacing={2}>
            {(['tour', 'hotel'] as Array<Part>).map((part) => (
              <AdjustmentField
                key={part}
                label={`${PART_LABELS[part]} adjustment`}
                value={bulk[part]}
                onChange={(value) => setBulk((current) => ({ ...current, [part]: value }))}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIsBulkOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={applyToAll}
            disabled={!(Object.values(bulk) as Array<string>).some((value) => ADJUSTMENT_PATTERN.test(value))}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {(changed.length > 0 || hasInvalid) && (
        <Paper
          elevation={6}
          sx={{ position: 'sticky', bottom: 16, mt: 2, px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}
        >
          <Typography flex={1}>
            {hasInvalid
              ? 'Fix the highlighted adjustments to save.'
              : `${changed.length} unsaved ${changed.length === 1 ? 'change' : 'changes'}`}
          </Typography>
          <Button onClick={() => setDrafts({})} disabled={updatePrices.isPending}>
            Discard
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={changed.length === 0 || hasInvalid || updatePrices.isPending}
          >
            Save
          </Button>
        </Paper>
      )}
    </Box>
  );
}
